import { Gavel, Home, Loader2, LogOut, Search, Settings, User, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { useAuth } from "~/lib/hooks/use-auth";
import { useViewer } from "~/lib/hooks/use-viewer";
import { cn } from "~/lib/utils";
import { useSidebar } from "./sidebar-context";

const navItems = [
	{ title: "Přehled", url: "/dashboard", icon: Home },
	{ title: "Vyhledávání", url: "/search", icon: Search },
	{ title: "Nastavení", url: "/nastaveni", icon: Settings },
];

export function AppSidebar() {
	const { user, signOut } = useAuth();
	const viewer = useViewer();
	const location = useLocation();
	const navigate = useNavigate();
	const { isOpen, close } = useSidebar();
	const [signingOut, setSigningOut] = useState(false);

	// Silent rollout: the explorer link appears only for entitled accounts.
	const items = viewer.data?.tenderExplorerEnabled
		? [
				navItems[0],
				{ title: "Průzkumník zakázek", url: "/dashboard/zakazky", icon: Gavel },
				...navItems.slice(1),
			]
		: navItems;

	const isActive = (url: string) => {
		if (url === "/dashboard") {
			// Keep "Přehled" from lighting up on /dashboard/zakazky.
			return location.pathname === "/dashboard";
		}
		return location.pathname.startsWith(url);
	};

	const handleSignOut = async () => {
		if (signingOut) return;
		setSigningOut(true);
		try {
			const { error } = await signOut();
			if (!error) {
				navigate("/login");
				return;
			}
		} catch {
			// The same user-facing recovery applies to rejected network requests.
		} finally {
			setSigningOut(false);
		}
		toast.error("Odhlášení se nezdařilo. Zkuste to prosím znovu.");
	};

	return (
		<>
			{/* Backdrop (mobile only) */}
			{isOpen && (
				<button
					type="button"
					tabIndex={-1}
					aria-label="Zavřít menu"
					className="fixed inset-0 z-20 cursor-default bg-gray-950/25 backdrop-blur-sm md:hidden"
					onClick={close}
				/>
			)}

			<aside
				className={cn(
					"glass-panel-strong fixed inset-y-3 left-3 z-30 flex w-64 flex-col overflow-hidden rounded-3xl transition-transform duration-200 ease-in-out",
					isOpen ? "translate-x-0" : "-translate-x-[calc(100%+1rem)]",
					"md:translate-x-0 md:z-10",
				)}
			>
				{/* Logo */}
				<div className="flex h-16 items-center justify-between px-6">
					<Link to="/dashboard">
						<img src="/logo.png" alt="FirmIndex" className="h-9" />
					</Link>
					<button
						type="button"
						onClick={close}
						aria-label="Zavřít menu"
						className="glass-control flex h-8 w-8 items-center justify-center rounded-full text-sidebar-muted transition-colors hover:text-sidebar-accent-foreground md:hidden"
					>
						<X className="h-5 w-5" />
					</button>
				</div>

				{/* Navigation */}
				<nav className="flex-1 overflow-y-auto px-3 py-3">
					<ul className="flex flex-col gap-1">
						{items.map((item) => (
							<li key={item.url}>
								<Link
									to={item.url}
									className={cn(
										"flex items-center gap-3 rounded-full px-3.5 py-2.5 text-sm font-medium transition-all",
										isActive(item.url)
											? "bg-primary-500 text-white shadow-sm"
											: "text-sidebar-muted hover:bg-white/55 hover:text-sidebar-accent-foreground",
									)}
								>
									<item.icon className="h-4 w-4" />
									<span>{item.title}</span>
								</Link>
							</li>
						))}
					</ul>
				</nav>

				{/* User footer */}
				<div className="px-3 pb-3">
					<div className="glass-control flex items-center gap-3 rounded-2xl p-3">
						<div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 shadow-sm">
							<User className="h-4 w-4 text-sidebar-muted" />
						</div>
						<div className="flex-1 min-w-0">
							<p className="truncate text-sm font-medium text-sidebar-foreground">
								{user?.email ?? "Uživatel"}
							</p>
							<button
								type="button"
								onClick={handleSignOut}
								disabled={signingOut}
								className="flex items-center gap-1 text-xs text-sidebar-muted hover:text-sidebar-foreground transition-colors"
							>
								{signingOut ? (
									<Loader2 className="h-3 w-3 animate-spin" />
								) : (
									<LogOut className="h-3 w-3" />
								)}
								<span className="underline">{signingOut ? "Odhlašuji…" : "Odhlásit se"}</span>
							</button>
						</div>
					</div>
				</div>
			</aside>
		</>
	);
}
